/* eslint-disable */
// UpgradePrompts.js
// Exports: <UpgradeModal> and <LockedPage>

import { API_URL } from '../../config';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// PRICE-006: appraisal ('prospects') moved from Pro-only to Solo-and-above
// — it's a growth tool, gating it behind Pro was backwards. Contractor
// management ('contractors') is no longer a lockable feature at all; it's
// unlocked for everyone and no longer marketed as a tier perk, so it's
// been dropped from these plan card feature lists entirely (still present
// where relevant on the property/settings screens themselves).
const PLAN_DETAILS = {
    solo: {
        name: 'Solo', monthlyPrice: '£8.00', annualPrice: '£80.00',
        color: '#0891b2', units: '4 let units',
        features: ['4 let units included','1 user','Reports & tax export',
                   'AI receipt & certificate scanning','Appraisal & deal pipeline',
                   'Compliance calendar & alerts','Data export'],
    },
    standard: {
        name: 'Standard', monthlyPrice: '£17.50', annualPrice: '£175.00',
        color: '#2563eb', units: '24 let units',
        features: ['24 let units included','Extra units at £1.00/mo each','3 users',
                   'Everything in Solo','Company expenses','Data export'],
    },
    pro: {
        name: 'Pro', monthlyPrice: '£35.00', annualPrice: '£350.00',
        color: '#7c3aed', units: '60 let units',
        features: ['60 let units included','Extra units at £0.80/mo (50p from unit 91)','10 users',
                   'Everything in Standard','Refurbishment projects',
                   'Service requests'],
    },
};

// Display-only: which plan cards are worth showing for a given locked
// feature. This does NOT decide access — the server's lockedFeatures list
// does that, via BillingContext.canAccess(). This only stops the modal
// showing, say, Solo as an "upgrade option" for a feature only Pro
// unlocks, which would be a real mismatch between the CTA and what
// clicking it actually gets you.
const FEATURE_UNLOCKING_PLANS = {
    reports:      ['solo','standard','pro'],
    ai:           ['solo','standard','pro'],
    prospects:    ['solo','standard','pro'],
    'multi-user': ['standard','pro'],
    projects:     ['pro'],
    service:      ['pro'],
    units:        ['solo','standard','pro'], // not a feature lock — used by PropertyLimitBanner
};


// Agency is contact us — shown separately in the modal footer
const AGENCY_CONTACT = {
    name: 'Agency', color: '#065f46',
    description: 'Outgrown 150 let units? Contact us for a tailored Agency plan.',
};

const FEATURE_MESSAGES = {
    reports:    { title: 'Unlock Reports & P&L',           body: 'Generate professional P&L reports for your accountant, track expenses by category, and get tax-ready summaries. Available from Solo.' },
    ai:         { title: 'Unlock AI Scanning',             body: 'Photograph a receipt or a compliance certificate and let casavello read the supplier, date, amount and category for you to confirm. Available from Solo.' },
    projects:   { title: 'Unlock Refurbishment Projects',  body: 'Track refurbishments and repairs from start to finish, with the spend recorded straight against the property. Available on Pro.' },
    calendar:   { title: 'Unlock Calendar & Compliance',   body: 'Stay on top of gas safety certificates, EPC renewals, HMO licence deadlines and tenancy renewals.' }, // unreachable — every plan including Free has this now, kept only so an old link/reference doesn't crash
    company:    { title: 'Unlock Company Expenses',        body: 'Track business-wide expenses separately from property costs. Perfect for Ltd company landlords.' },
    prospects:  { title: 'Unlock Appraisal & Deal Pipeline', body: 'Analyse potential purchases before you buy. Model yields, ROI, refurb costs and financing — a genuine growth tool for reaching your next tier, not something we hold back until you get there. Available from Solo.' },
    'multi-user': { title: 'Unlock Team Access',           body: 'Invite letting agents, accountants or assistants. Control exactly what each person can see. Available from Standard.' },
    service:      { title: 'Unlock Service Requests',       body: 'Log maintenance issues, assign contractors, track jobs from report to resolution, and roll completed work straight into expenses. Available on Pro.' },
    units:        { title: 'Need more let units?',          body: "You've reached the let-unit limit on your current plan. Every tier below raises that limit — pick whichever gives you the room your portfolio needs." },
};

async function startCheckout(planName, interval, setLoading, setError) {
    setLoading(planName + interval);
    setError('');
    try {
        const BASE  = API_URL;
        const token = localStorage.getItem('token');
        const res   = await fetch(`${BASE}/api/billing/create-checkout-session`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ planName, interval }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Checkout failed');
        window.location.href = data.url;
    } catch (e) {
        setError(e.message || 'Could not start checkout. Please try again.');
        setLoading(null);
    }
}

// ── UpgradeModal ─────────────────────────────────────────────
export function UpgradeModal({ feature, onClose }) {
    const [interval, setInterval]   = useState('month');
    const [loadingPlan, setLoading] = useState(null);
    const [error, setError]         = useState('');

    const msg = FEATURE_MESSAGES[feature] || { title: 'Upgrade your plan', body: 'This feature is available on paid plans.' };
    const relevantPlans = FEATURE_UNLOCKING_PLANS[feature] || ['solo','standard','pro'];

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
        }} onClick={onClose}>
            <div style={{
                background: '#fff', borderRadius: '1rem', padding: '2rem',
                maxWidth: 680, width: '100%', maxHeight: '90vh', overflowY: 'auto',
                boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }} onClick={e => e.stopPropagation()}>

                {/* Brand bar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '1rem', marginBottom: '1.25rem', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
                        <img src="/casavello-icon-500.png" alt="" style={{ height: 30, width: 30, objectFit: 'contain' }} />
                        <div>
                            <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#166534', lineHeight: 1.1 }}>casavello</div>
                            <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>Your entire property portfolio, effortlessly managed</div>
                        </div>
                    </div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280', lineHeight: 1, padding: 0 }}>×</button>
                </div>

                {/* Header */}
                <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ fontSize: '1.75rem', marginBottom: '0.25rem' }}>🔒</div>
                    <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#111', fontWeight: 700 }}>{msg.title}</h2>
                </div>
                <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.9375rem', lineHeight: 1.6 }}>{msg.body}</p>

                {/* Billing toggle */}
                <div style={{ display: 'flex', gap: '0.25rem', background: '#f3f4f6', borderRadius: '0.5rem', padding: '0.25rem', width: 'fit-content', marginBottom: '1.5rem' }}>
                    {[['month','Monthly'],['year','Annual — 2 months free 🎉']].map(([key, label]) => (
                        <button key={key} onClick={() => setInterval(key)} style={{
                            padding: '0.375rem 1rem', borderRadius: '0.375rem', border: 'none', cursor: 'pointer',
                            fontWeight: 600, fontSize: '0.875rem',
                            background: interval === key ? '#fff' : 'transparent',
                            color:      interval === key ? '#111' : '#6b7280',
                            boxShadow:  interval === key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                        }}>{label}</button>
                    ))}
                </div>

                {/* Plan cards — filtered to only the plans that actually unlock this feature */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                    {Object.entries(PLAN_DETAILS).filter(([key]) => relevantPlans.includes(key)).map(([key, plan]) => {
                        const price     = interval === 'year' ? `${plan.annualPrice}/yr` : `${plan.monthlyPrice}/mo`;
                        const isLoading = loadingPlan === key + interval;
                        return (
                            <div key={key} style={{ border: `2px solid ${plan.color}`, borderRadius: '0.75rem', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                <div>
                                    <div style={{ fontWeight: 700, color: plan.color, fontSize: '1rem' }}>{plan.name}</div>
                                    <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{plan.units}</div>
                                    <div style={{ fontWeight: 800, fontSize: '1.375rem', color: '#111', marginTop: '0.25rem' }}>{price}</div>
                                    {interval === 'year' && <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: 600 }}>2 months free</div>}
                                </div>
                                <ul style={{ margin: 0, padding: '0 0 0 1rem', fontSize: '0.8125rem', color: '#374151', flex: 1 }}>
                                    {plan.features.map(f => <li key={f} style={{ marginBottom: '0.2rem' }}>{f}</li>)}
                                </ul>
                                <button
                                    onClick={() => startCheckout(key, interval, setLoading, setError)}
                                    disabled={!!loadingPlan}
                                    style={{
                                        background: plan.color, color: '#fff', border: 'none',
                                        borderRadius: '0.5rem', padding: '0.625rem', fontWeight: 700,
                                        fontSize: '0.875rem', cursor: 'pointer',
                                        opacity: loadingPlan && !isLoading ? 0.6 : 1,
                                    }}
                                >{isLoading ? 'Redirecting...' : 'Start 14-day trial'}</button>
                            </div>
                        );
                    })}
                </div>

                {error && <p style={{ color: '#dc2626', fontSize: '0.875rem', textAlign: 'center' }}>{error}</p>}
                {/* Agency contact prompt */}
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginTop: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: '0.8125rem', color: '#166534' }}>
                        🏢 <strong>Outgrown 150 let units?</strong> Contact us for a tailored Agency plan.
                    </div>
                    <a href="/contact" style={{ background: '#166534', color: '#fff', borderRadius: '0.375rem', padding: '0.375rem 0.75rem', fontWeight: 700, fontSize: '0.8125rem', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                        Contact Us →
                    </a>
                </div>
                <p style={{ fontSize: '0.75rem', color: '#9ca3af', textAlign: 'center', margin: '0.5rem 0 0' }}>
                    No card required to start trial · Cancel anytime · Prices ex. VAT
                </p>
                <button onClick={onClose} style={{ display: 'block', margin: '0.75rem auto 0', background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '0.875rem', textDecoration: 'underline' }}>
                    Maybe later
                </button>
            </div>
        </div>
    );
}

// ── LockedPage ───────────────────────────────────────────────
export function LockedPage({ feature }) {
    const [showModal, setShowModal] = useState(false);
    const navigate = useNavigate();

    const LABELS = {
        reports: 'Reports & P&L', projects: 'Refurbishment Projects',
        service: 'Service Requests', ai: 'AI Scanning', company: 'Company Expenses',
        prospects: 'Appraisal & Deal Pipeline', 'multi-user': 'Team Access',
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: '2rem', textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔒</div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111', marginBottom: '0.5rem' }}>{LABELS[feature] || 'This Feature'}</h2>
            <p style={{ color: '#6b7280', maxWidth: 420, marginBottom: '1.5rem', lineHeight: 1.6 }}>
                {FEATURE_MESSAGES[feature]?.body || 'This feature is available on paid plans.'}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                <button onClick={() => setShowModal(true)} style={{ background: '#166534', color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.75rem 1.5rem', fontWeight: 700, fontSize: '0.9375rem', cursor: 'pointer' }}>
                    View Plans
                </button>
                <button onClick={() => navigate('/')} style={{ background: 'transparent', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '0.5rem', padding: '0.75rem 1.5rem', fontWeight: 600, fontSize: '0.9375rem', cursor: 'pointer' }}>
                    Back to Dashboard
                </button>
            </div>
            {showModal && <UpgradeModal feature={feature} onClose={() => setShowModal(false)} />}
        </div>
    );
}
